/**
 * AddressAutocomplete — search-as-you-type address picker.
 *
 * Two backends, picked at runtime based on REACT_APP_MAPBOX_TOKEN:
 *
 *  1. MAPBOX Search Box API — when a token is set. Best quality results,
 *     100k req/month free.
 *
 *  2. NOMINATIM (OpenStreetMap) — when no token is set. Public, free, no
 *     account. Rate-limited to ~1 req/sec; we already debounce input.
 *     Usage policy: https://operations.osmfoundation.org/policies/nominatim/
 *
 * Same component surface; callers don't need to know which backend is live.
 */
import { useEffect, useRef, useState } from 'react';
import { MapPin, Search, Navigation } from 'lucide-react';
import { MAPBOX_TOKEN, isMapboxConfigured } from '../../config/mapbox';

export interface AutocompleteResult {
  address: string;
  latitude: number;
  longitude: number;
  mapbox_id?: string;
}

interface SuggestionItem {
  id: string;       // mapbox_id OR nominatim place_id
  name: string;
  full_address?: string;
  place_formatted?: string;
  /** Pre-resolved coordinates (only set by Nominatim — Mapbox needs a separate /retrieve call). */
  latitude?: number;
  longitude?: number;
}

interface Props {
  value?: string;
  placeholder?: string;
  proximity?: { latitude: number; longitude: number };
  country?: string; // ISO-2, defaults to "in"
  onSelect: (r: AutocompleteResult) => void;
  onUseMyLocation?: () => void;
  active?: boolean;
  dotColor?: string;
}

const SESSION_TOKEN = (() => {
  if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) {
    return (crypto as any).randomUUID();
  }
  return Math.random().toString(36).slice(2);
})();

// ---------------------------------------------------------------------------
// Nominatim helpers (OSM fallback)
// ---------------------------------------------------------------------------
const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org';

async function nominatimSearch(q: string, country: string, proximity?: { latitude: number; longitude: number }) {
  const params = new URLSearchParams({
    q,
    format: 'json',
    addressdetails: '1',
    limit: '8',
    countrycodes: country,
  });
  // Nominatim "viewbox" biases results around a point (left,top,right,bottom)
  if (proximity) {
    const d = 0.5; // ~50km bias box
    params.set('viewbox', [
      proximity.longitude - d,
      proximity.latitude + d,
      proximity.longitude + d,
      proximity.latitude - d,
    ].join(','));
    params.set('bounded', '0');
  }
  const res = await fetch(`${NOMINATIM_BASE}/search?${params.toString()}`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) return [] as SuggestionItem[];
  const rows = await res.json();
  return (rows as any[]).map((r): SuggestionItem => ({
    id: String(r.place_id),
    name: r.display_name?.split(',')[0] ?? r.display_name ?? '',
    full_address: r.display_name,
    place_formatted: r.display_name,
    latitude: parseFloat(r.lat),
    longitude: parseFloat(r.lon),
  }));
}

async function nominatimReverse(lat: number, lng: number): Promise<string> {
  try {
    const res = await fetch(
      `${NOMINATIM_BASE}/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`,
      { headers: { Accept: 'application/json' } },
    );
    if (!res.ok) return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    const data = await res.json();
    return data.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  } catch {
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  }
}

export async function reverseGeocode(lat: number, lng: number): Promise<string> {
  if (isMapboxConfigured()) {
    try {
      const url = `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${lng}&latitude=${lat}&access_token=${MAPBOX_TOKEN}`;
      const res = await fetch(url);
      if (!res.ok) return nominatimReverse(lat, lng);
      const data = await res.json();
      const f = data.features?.[0];
      return f?.properties?.full_address || f?.properties?.place_formatted || f?.place_name || nominatimReverse(lat, lng);
    } catch {
      return nominatimReverse(lat, lng);
    }
  }
  return nominatimReverse(lat, lng);
}

const AddressAutocomplete: React.FC<Props> = ({
  value,
  placeholder = 'Search location…',
  proximity,
  country = 'in',
  onSelect,
  onUseMyLocation,
  active = false,
  dotColor = '#0C831F',
}) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<any>();

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  const fetchSuggestions = async (q: string) => {
    if (q.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    try {
      let items: SuggestionItem[] = [];
      if (isMapboxConfigured()) {
        const params = new URLSearchParams({
          q,
          access_token: MAPBOX_TOKEN,
          session_token: SESSION_TOKEN,
          language: 'en',
          country,
          limit: '8',
        });
        if (proximity) params.set('proximity', `${proximity.longitude},${proximity.latitude}`);
        const res = await fetch(`https://api.mapbox.com/search/searchbox/v1/suggest?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          items = (data.suggestions || []).map((s: any): SuggestionItem => ({
            id: s.mapbox_id,
            name: s.name,
            full_address: s.full_address,
            place_formatted: s.place_formatted,
          }));
        }
      } else {
        items = await nominatimSearch(q, country, proximity);
      }
      setSuggestions(items);
      setOpen(items.length > 0);
    } catch {
      setSuggestions([]);
    }
  };

  const onChange = (q: string) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Nominatim asks for ≤1 req/s — use a slightly longer debounce when
    // we're on the OSM fallback so we never burst.
    const wait = isMapboxConfigured() ? 300 : 600;
    debounceRef.current = setTimeout(() => fetchSuggestions(q), wait);
  };

  const pick = async (s: SuggestionItem) => {
    setOpen(false);
    setQuery(s.full_address || s.place_formatted || s.name);
    // Nominatim suggestions already carry coordinates — emit straight away.
    if (typeof s.latitude === 'number' && typeof s.longitude === 'number') {
      onSelect({
        address: s.full_address || s.place_formatted || s.name,
        latitude: s.latitude,
        longitude: s.longitude,
        mapbox_id: s.id,
      });
      return;
    }
    // Mapbox needs a /retrieve round-trip to resolve the coordinates.
    try {
      const url = `https://api.mapbox.com/search/searchbox/v1/retrieve/${s.id}?access_token=${MAPBOX_TOKEN}&session_token=${SESSION_TOKEN}`;
      const res = await fetch(url);
      const data = await res.json();
      const f = data.features?.[0];
      if (!f) return;
      const [lng, lat] = f.geometry.coordinates;
      onSelect({
        address: f.properties?.full_address || f.properties?.place_formatted || s.name,
        latitude: lat,
        longitude: lng,
        mapbox_id: s.id,
      });
    } catch {
      /* ignore */
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: '#F0F0F5',
          borderRadius: 12,
          padding: '12px 14px',
          boxShadow: active ? `0 0 0 2px ${dotColor}` : 'none',
          transition: 'box-shadow .15s ease',
        }}
      >
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: dotColor }} />
        <Search size={16} color="#999" />
        <input
          value={query}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => suggestions.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder}
          style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: 14 }}
        />
        {onUseMyLocation && (
          <button
            type="button"
            onClick={onUseMyLocation}
            title="Use my current location"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: dotColor, display: 'flex' }}
          >
            <Navigation size={18} />
          </button>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            background: 'white',
            border: '1px solid #E8E8E8',
            borderRadius: 12,
            marginTop: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            maxHeight: 280,
            overflowY: 'auto',
          }}
        >
          {suggestions.map((s) => (
            <div
              key={s.id}
              onMouseDown={() => pick(s)}
              style={{
                padding: '10px 14px',
                fontSize: 13,
                cursor: 'pointer',
                borderBottom: '1px solid #f3f3f3',
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
              }}
            >
              <MapPin size={14} style={{ marginTop: 2, color: '#888', flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 500 }}>{s.name}</div>
                {s.place_formatted && (
                  <div style={{ fontSize: 12, color: '#777' }}>{s.place_formatted}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;

